import { TenantSeniorityStatusEnum } from "api/real-estate";

/**
 * Équivalent de DateTime.AddMonths du back (.NET) : borné au dernier jour du mois cible.
 * setMonth natif déborderait (ex. 31 août + 6 mois → 3 mars) et divergerait du back.
 */
const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDayOfMonth));
  return result;
};

/**
 * Statut d'ancienneté du locataire au début du contrat GLI.
 * Mêmes bornes que le back (Locataire.GetStatutAnciennete) : ne pas modifier l'un sans l'autre.
 * - entrée prévue plus de 6 mois après le début du contrat : refusé pour l'instant → à souscrire à l'approche de l'entrée
 * - moins de 16 jours (ou entrée à venir dans les 6 mois) : nouveau → dossier revenus exigé
 * - entre 16 jours et 6 mois : refusé d'office (zone anti-sélection)
 * - plus de 6 mois : en place → seule la déclaration « à jour de ses loyers » est exigée
 */
export const getTenantSeniorityStatus = (
  checkInDate: string | Date | undefined,
  contractStartDate?: Date | null
): TenantSeniorityStatusEnum | undefined => {
  if (!checkInDate) return undefined;
  const checkIn = new Date(checkInDate);
  if (isNaN(checkIn.getTime())) return undefined;

  const reference = contractStartDate ?? new Date();
  const sixteenDaysBefore = new Date(reference);
  sixteenDaysBefore.setDate(sixteenDaysBefore.getDate() - 16);
  const sixMonthsBefore = addMonths(reference, -6);
  const sixMonthsAfter = addMonths(reference, 6);

  if (checkIn > sixMonthsAfter) return TenantSeniorityStatusEnum.FutureEntry;
  if (checkIn > sixteenDaysBefore) return TenantSeniorityStatusEnum.New;
  if (checkIn >= sixMonthsBefore) return TenantSeniorityStatusEnum.RefusedPeriod;
  return TenantSeniorityStatusEnum.InPlace;
};
